import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { workspaceSlug } from "../lib/hosts";
import { useState } from "react";
import { toast } from "sonner";
import { useWorkspace } from "../lib/workspaces";
import { useArchiveProject, useDeleteProject, useDeletionImpact, useProject } from "../lib/projects";
import { useMe, meUser } from "../lib/auth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ConfirmDialog } from "../components/workspaces/ConfirmDialog";
import { ProjectGeneralSettings } from "../components/projects/ProjectGeneralSettings";
import { ProjectAccess } from "../components/projects/ProjectAccess";
import { ProjectTrash } from "../components/projects/ProjectTrash";
import { ProjectWorkflow } from "../components/projects/ProjectWorkflow";
import { ProjectBoardSettings } from "../components/projects/ProjectBoardSettings";
import { ProjectSlack } from "../components/slack/ProjectSlack";
import { ProjectUnreachable } from "../components/projects/ProjectStates";
import { isNotFound, NotAllowed, Unavailable } from "../components/ui/states";
import { strings } from "../lib/strings";
import { PageTitle } from "../lib/title";
import { ToWorkspaces } from "../components/workspaces/ToWorkspaces";

const words = strings.projects.settings;

/**
 * A project's settings (BUILD_SPEC §10.2, §8.3), for those who configure it: its lead, its admins and Steer's
 * Administrators. Its own settings, who sees it, its trash, its archive, and, for Administrators, its deletion.
 */
function ProjectSettingsPage() {
  const { projectKey } = useParams({ from: "/admin/p/$projectKey/settings" });
  const slug = workspaceSlug();
  const me = useMe();
  const view = useWorkspace(slug);
  const navigate = useNavigate();
  const project = useProject(projectKey);
  const projectId = project.data?.project.id ?? "";
  const archive = useArchiveProject(slug, projectId);
  const remove = useDeleteProject(slug, projectId);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const impact = useDeletionImpact(projectId, confirmDelete);

  if (view.isLoading || me.isLoading || project.isLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-3 py-12">
        <div className="h-8 w-48 animate-pulse rounded bg-[hsl(var(--muted))]" />
        <div className="h-40 animate-pulse rounded bg-[hsl(var(--muted))]" />
      </div>
    );
  }
  const user = meUser(me);
  if (!view.data || !user) return <ToWorkspaces />;
  if (project.data === undefined)
    return isNotFound(project.error) ? (
      <ProjectUnreachable project={undefined} />
    ) : (
      <div className="mx-auto max-w-3xl px-6 py-12">
        <Unavailable what={strings.projects.list.thisProject} onRetry={() => void project.refetch()} page />
      </div>
    );
  if (!project.data.canManage)
    return (
      <div className="mx-auto max-w-3xl px-6 py-12">
        <NotAllowed who={strings.states.who.projectAdmins} what={words.manage} page />
      </div>
    );
  const { project: current } = project.data;
  const administrator = view.data.role === "administrator";
  const archived = current.archivedAt !== null;

  return (
    <div className="mx-auto max-w-3xl space-y-6 py-12">
      <PageTitle name={`${words.title} · ${current.name}`} />
      <header className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]">
        <Link to="/admin/p/$projectKey" params={{ projectKey: current.key }} className="hover:underline">
          {current.name}
        </Link>
        <span>·</span>
        <h1 className="text-[hsl(var(--foreground))]">{words.title}</h1>
      </header>

      <ProjectGeneralSettings
        slug={slug}
        project={current}
        onKeyChanged={(key) =>
          void navigate({ to: "/admin/p/$projectKey/settings", params: { projectKey: key }, replace: true })
        }
      />
      <ProjectWorkflow slug={slug} projectId={current.id} statuses={project.data.statuses} disabled={archived} />
      <ProjectBoardSettings
        projectId={current.id}
        method={current.method}
        statuses={project.data.statuses}
        disabled={archived}
      />
      <ProjectAccess slug={slug} project={current} meId={user.id} />
      <Card>
        <CardHeader>
          <CardTitle>{strings.automation.title}</CardTitle>
          <CardDescription>{strings.automation.settingsBody}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild variant="outline">
            <Link to="/admin/p/$projectKey/automation" params={{ projectKey: current.key }}>
              {strings.automation.open}
            </Link>
          </Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>{strings.imports.title}</CardTitle>
          <CardDescription>{strings.imports.settingsBody}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild variant="outline">
            <Link to="/admin/p/$projectKey/import" params={{ projectKey: current.key }}>
              {strings.imports.open}
            </Link>
          </Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>{strings.exports.title}</CardTitle>
          <CardDescription>{strings.exports.settingsBody}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild variant="outline">
            <Link to="/admin/exports" search={{ project: current.id }}>
              {strings.exports.open}
            </Link>
          </Button>
        </CardContent>
      </Card>
      <ProjectSlack projectId={current.id} />
      <ProjectTrash projectId={current.id} />

      <Card>
        <CardHeader>
          <CardTitle>{words.archiveSection}</CardTitle>
          <CardDescription>{archived ? words.unarchiveBody : words.archiveBody}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant="outline"
            disabled={archive.isPending}
            onClick={() =>
              archive.mutate(!archived, {
                onSuccess: () =>
                  toast.success(
                    (archived ? words.unarchivedToast : words.archivedToast).replace("{name}", current.name),
                  ),
                onError: () => toast.error(words.saveFailed),
              })
            }
          >
            {archived ? words.unarchiveAction : words.archiveAction}
          </Button>
        </CardContent>
      </Card>

      {administrator && (
        <Card className="border-[hsl(var(--destructive))]">
          <CardHeader>
            <CardTitle className="text-[hsl(var(--destructive-text))]">{words.dangerTitle}</CardTitle>
            <CardDescription>{words.deleteBody}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="destructive" onClick={() => setConfirmDelete(true)} disabled={remove.isPending}>
              {words.deleteAction}
            </Button>
          </CardContent>
        </Card>
      )}

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={words.deleteConfirmTitle.replace("{name}", current.name)}
        description={words.deleteConfirmBody
          .replace("{name}", current.name)
          .replace("{count}", impact.data === undefined ? "…" : String(impact.data.issues))}
        confirmLabel={words.deleteConfirmAction}
        destructive
        busy={remove.isPending}
        onConfirm={() =>
          remove.mutate(undefined, {
            onSuccess: () => {
              toast.success(words.deletedToast);
              setConfirmDelete(false);
              void navigate({ to: "/admin" });
            },
            onError: () => toast.error(words.deleteFailed),
          })
        }
      />
    </div>
  );
}

export const Route = createFileRoute("/admin/p/$projectKey/settings")({ component: ProjectSettingsPage });
